/**
 * The body of one opened run (D11b): calm sections, each answering one question.
 *
 *   1. Ergebnis      — what happened, and why, in words.
 *      Prüffall      — move-in review runs only (S1): why the address was left
 *                      alone, the current case and the answer — its own
 *                      component (`AutomationMoveInReview`), see there.
 *   2. Ziel          — who it was about, and under which contract.
 *   3. Ausführung    — when, how it was started, which attempt.
 *   4. Nachricht     — e-mail automations only: the subject that went out and
 *                      the template it came from.
 *   5. Ausgelöst von — the admin behind a manual run, or the system.
 *   6. Adressänderung — the move-in address only: before and after (D11a).
 *      Rechnung      — a payment confirmation's claim run only (PC6), right
 *                      after "Ergebnis": the invoice, the period and the total
 *                      as the e-mail stated them, when the send was claimed
 *                      ("vorgemerkt" — before the provider was called, so
 *                      never "begonnen") and when its outcome was stored.
 *                      Its outcome word and sentence lead "Ergebnis".
 *
 * EVERY VALUE IS A FIELD OF THE DETAIL RESPONSE (D7, D11a), shown or left out —
 * never derived from something else. In particular: the subject is the one the
 * run STORED, never a template rendered again; the template is a source and a
 * version, never its body; the contract is the one the run stored or none,
 * never "the tenant's current one"; the address change is the snapshot, never
 * today's address. The one exception is labelled as such: the review's
 * "Aktueller Prüffall" is the tenant's case as it is NOW (S1), shown under its
 * own heading and never as part of the run.
 *
 * WHAT IS NOT SHOWN, on purpose: the delivery claim (its id has no meaning to a
 * reader, and its internals — provider message id, recipient, provider status —
 * never leave the server), the tenant's e-mail address, and any raw JSON. An
 * empty field is left out rather than printed as "null" or "—". A payment
 * confirmation shows no recipient address, no idempotency key, no provider or
 * Bexio id — the server does not even send them (PC6) — and NO ACTION: there is
 * no "erneut senden", no retry, no test send. At most once is the contract.
 */
import React from "react";

import { formatDateTime } from "../../../utils/datetime";
import AutomationMoveInReview from "./AutomationMoveInReview";
import AutomationRunAddressChange from "./AutomationRunAddressChange";
import {
  AutomationRunStatusBadge,
  AutomationRunTestBadge,
} from "./AutomationRunStatusBadge";
import {
  automationActionLabel,
  automationTriggerLabel,
} from "./automationLabels";
import {
  automationRunActor,
  automationRunReasons,
  automationRunStatusNote,
  automationRunTarget,
  automationRunTemplateLabel,
  isKnownAutomationRunReason,
} from "./automationRunLabels";
import {
  automationRunPresentation,
  invoiceConfirmationOutcome,
  invoiceConfirmationRunNote,
} from "./automationRunOutcome";

function Section({ title, children }) {
  return (
    <section
      aria-label={title}
      className="flex flex-col gap-2.5 rounded-[12px] border border-border bg-card p-3.5"
    >
      <h3 className="m-0 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      {children}
    </section>
  );
}

function Fields({ children }) {
  return <dl className="m-0 grid grid-cols-1 gap-x-4 gap-y-2.5 sm:grid-cols-2">{children}</dl>;
}

/** One label/value pair. Renders nothing when there is no value. */
function Field({ label, children, wide = false }) {
  if (children === null || children === undefined || children === "" || children === false) {
    return null;
  }
  return (
    <div className={wide ? "min-w-0 sm:col-span-2" : "min-w-0"}>
      <dt className="text-[11px] text-muted-foreground">{label}</dt>
      <dd className="m-0 mt-0.5 break-words text-[12.5px] text-foreground">{children}</dd>
    </div>
  );
}

function Time({ value }) {
  if (!value) return null;
  return <time dateTime={value}>{formatDateTime(value)}</time>;
}

function ResultSection({ run }) {
  const presentation = automationRunPresentation(run);
  // The outcome's own sentence replaces the generic indeterminate note: one
  // explanation, never two that could read differently. A ledger outcome
  // never takes the generic note at all — the ledger is the delivery truth,
  // whatever happened to the run's own row.
  const note = invoiceConfirmationOutcome(run)
    ? invoiceConfirmationRunNote(run)
    : presentation.text
      ? ""
      : automationRunStatusNote(run);
  const reasons = automationRunReasons(run);
  const inventory = run.baseline_inventory;
  const hasInventory =
    inventory !== null && inventory !== undefined && Number.isInteger(inventory.invoice_count);
  return (
    <Section title="Ergebnis">
      <div className="flex flex-wrap items-center gap-1.5">
        <AutomationRunStatusBadge status={run.status} badge={presentation.badge} />
        {run.is_test ? <AutomationRunTestBadge /> : null}
      </div>
      {presentation.title ? (
        <p className="m-0 text-[13px] font-semibold text-foreground">{presentation.title}</p>
      ) : null}
      {presentation.text ? (
        <p className="m-0 text-[12.5px] text-foreground">{presentation.text}</p>
      ) : null}
      {note ? <p className="m-0 text-[12.5px] text-foreground">{note}</p> : null}
      {hasInventory ? (
        <Fields>
          <Field label="Erfasste Rechnungen">{String(inventory.invoice_count)}</Field>
        </Fields>
      ) : null}
      {hasInventory ? (
        <p className="m-0 text-[12px] text-muted-foreground">
          Gezählt beim Öffnen: die Rechnungen, die diese Abgrenzung erfasst hat –
          auch solche, die vor ihrem Abschluss erstellt, aber erst danach in Bexio
          angelegt wurden. Rechnungen, die diese Automation schon vorher erfasst
          hatte (etwa bei einer früheren Aktivierung), zählen hier nicht erneut;
          für sie gilt weiterhin, was damals festgehalten wurde.
        </p>
      ) : null}
      {reasons.length || run.error_message_safe ? (
        <Fields>
          {reasons.map((reason) => (
            <Field
              key={reason.code}
              label={reason.code === run.skip_reason ? "Grund" : "Fehler"}
              wide
            >
              <span>{reason.label}</span>
              {isKnownAutomationRunReason(reason.code) ? (
                <span className="mt-0.5 block font-mono text-[11px] text-muted-foreground">
                  Code: {reason.code}
                </span>
              ) : null}
            </Field>
          ))}
          <Field label="Meldung" wide>
            {run.error_message_safe || null}
          </Field>
        </Fields>
      ) : null}
    </Section>
  );
}

/**
 * The invoice a payment confirmation's claim run was about (PC6): every value
 * is the server's projection of the ledger row — the reference as Bexio
 * numbered it, the period and the total in the e-mail's own words — and the
 * two moments the claim recorded. Nothing here is an action.
 */
function InvoiceConfirmationSection({ run }) {
  const confirmation = run.invoice_confirmation;
  const outcome = invoiceConfirmationOutcome(run);
  if (!confirmation || !outcome) return null;
  const endedAt = outcome.timeField ? confirmation[outcome.timeField] : null;
  return (
    <Section title="Rechnung">
      <Fields>
        <Field label="Rechnung">{confirmation.invoice_reference || null}</Field>
        <Field label="Abrechnungszeitraum">{confirmation.period_label || null}</Field>
        <Field label="Betrag">{confirmation.amount_label || null}</Field>
        <Field label="Versand vorgemerkt">
          {confirmation.claimed_at ? <Time value={confirmation.claimed_at} /> : null}
        </Field>
        <Field label={outcome.timeLabel}>
          {endedAt ? <Time value={endedAt} /> : null}
        </Field>
      </Fields>
    </Section>
  );
}

// Targets that are not a person: the pre-D6d aggregate, and the payment
// confirmation's activation inventory and organisation-wide stop (PC5).
const TARGET_KINDS_WITHOUT_PERSON = ["aggregate", "baseline", "organization"];

function TargetSection({ run }) {
  const target = automationRunTarget(run);
  return (
    <Section title="Ziel">
      <Fields>
        <Field label={TARGET_KINDS_WITHOUT_PERSON.includes(target.kind) ? "Art" : "Mieter"} wide>
          {target.text}
        </Field>
        <Field label="Mietverhältnis">{run.tenancy?.reference_number || null}</Field>
      </Fields>
      {target.kind === "aggregate" ? (
        <p className="m-0 text-[12px] text-muted-foreground">
          Diese Ausführung stammt aus der Zeit vor den Einzelausführungen: Ein Lauf
          wurde als eine Zeile ohne einzelnen Mieter gespeichert.
        </p>
      ) : null}
    </Section>
  );
}

function ExecutionSection({ run, actionType }) {
  const action = actionType && actionType !== "send_email" ? automationActionLabel(actionType) : "";
  const attempt = Number(run.attempt);
  return (
    <Section title="Ausführung">
      <Fields>
        <Field label="Aktion" wide>
          {action || null}
        </Field>
        <Field label="Auslöser">{automationTriggerLabel(run.trigger_type) || null}</Field>
        <Field label="Versuch">{Number.isFinite(attempt) && attempt > 0 ? String(attempt) : null}</Field>
        <Field label="Gestartet">
          <Time value={run.started_at} />
        </Field>
        <Field label="Beendet">
          {run.finished_at ? <Time value={run.finished_at} /> : "Nicht abgeschlossen"}
        </Field>
        <Field label="Testlauf">{run.is_test ? "Ja" : null}</Field>
      </Fields>
    </Section>
  );
}

function MessageSection({ run }) {
  const template = automationRunTemplateLabel(run);
  const subject = run.rendered_subject || "";
  return (
    <Section title="Nachricht">
      {subject || template ? (
        <Fields>
          <Field label="Betreff" wide>
            {subject || null}
          </Field>
          <Field label="Vorlage" wide>
            {template || null}
          </Field>
        </Fields>
      ) : (
        <p className="m-0 text-[12.5px] text-muted-foreground">
          Für diese Ausführung wurde keine Nachricht erstellt.
        </p>
      )}
    </Section>
  );
}

function ActorSection({ run }) {
  const actor = automationRunActor(run);
  return (
    <Section title="Ausgelöst von">
      <p className="m-0 text-[12.5px] text-foreground">
        <span className={actor.kind === "user" ? "font-semibold" : ""}>{actor.name}</span>
        {actor.email ? (
          <span className="block break-all text-[11.5px] text-muted-foreground">
            {actor.email}
          </span>
        ) : null}
      </p>
    </Section>
  );
}

/**
 * Which kind of automation the run belongs to decides two sections.
 *
 * Told by the automation's `action_type` (the registry's answer, from the list
 * row the page holds). Without it — a deep link answered before the list — the
 * run's own fields decide: a stored subject or template means an e-mail run, a
 * stored address change a data run. Never the key or the category.
 *
 * `review` is the move-in review read for this run (S1, `useMoveInReview`);
 * the review component decides itself whether it has anything to show.
 */
export default function AutomationRunDetailSections({ run, actionType, review = null }) {
  if (!run) return null;
  const sendsEmail =
    actionType === "send_email" ||
    (!actionType && !!(run.template_source || run.rendered_subject));
  // An admin who KEPT the address wrote none, and the answer's own section
  // shows the two addresses frozen with it — "nothing stored" would read as a
  // gap where there is a decision (S1).
  const keptByReview = run.review_resolution?.decision === "kept";
  const changesAddress =
    !keptByReview &&
    (actionType === "update_tenant_address" ||
      (!actionType && run.address_change !== null && run.address_change !== undefined));

  return (
    <div className="flex flex-col gap-3">
      <ResultSection run={run} />
      <InvoiceConfirmationSection run={run} />
      <AutomationMoveInReview run={run} review={review} />
      <TargetSection run={run} />
      <ExecutionSection run={run} actionType={actionType} />
      {sendsEmail ? <MessageSection run={run} /> : null}
      <ActorSection run={run} />
      {changesAddress ? (
        <Section title="Adressänderung">
          <AutomationRunAddressChange change={run.address_change || null} />
        </Section>
      ) : null}
    </div>
  );
}
